import { type SyntheticEvent, type TouchEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, ImageIcon, Loader2, Maximize2, Minimize2, Trash2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/shared/ui/dialog';

export interface AttachmentPhoto {
  id: string;
  file: string;
  file_url: string | null;
}

interface AttachmentPhotoGalleryProps {
  attachments: AttachmentPhoto[];
  maxVisible?: number;
  gridClassName?: string;
  label?: string | null;
  onDelete?: (attachmentId: string) => void;
  isDeleting?: boolean;
}

const IMAGE_EXTENSION_PATTERN = /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)(\?.*)?$/i;
const PHOTO_UNAVAILABLE_SRC = '/static/images/photo_unavailable.jpg';
const MIN_SWIPE_DISTANCE = 50;

export const isLikelyImageAttachment = (value: string | null | undefined): boolean => {
  if (!value) return false;
  if (value.startsWith('blob:') || value.startsWith('data:image/')) return true;
  const sanitized = value.split('#')[0];
  return IMAGE_EXTENSION_PATTERN.test(sanitized);
};

const attachmentUrl = (attachment: AttachmentPhoto) => attachment.file_url ?? attachment.file;
const attachmentName = (attachment: AttachmentPhoto) =>
  attachment.file.split('/').pop()?.split('?')[0] ?? 'Image attachment';

const handleImageError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
  const image = event.currentTarget;
  if (image.dataset.fallbackApplied === 'true') return;

  image.dataset.fallbackApplied = 'true';
  image.src = PHOTO_UNAVAILABLE_SRC;
  image.alt = 'Photo unavailable';
  image.classList.remove('object-cover');
  image.classList.add('object-contain', 'bg-white');
};

export const AttachmentPhotoGallery = ({
  attachments,
  maxVisible = 6,
  gridClassName = 'grid grid-cols-3 sm:grid-cols-4 gap-2',
  label = 'Photos',
  onDelete,
  isDeleting = false,
}: AttachmentPhotoGalleryProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const swipeRef = useRef<{ id: number; x: number; y: number; photoId: string } | null>(null);

  const imageAttachments = useMemo(
    () => attachments.filter((attachment) => isLikelyImageAttachment(attachmentUrl(attachment))),
    [attachments],
  );

  const visibleAttachments = imageAttachments.slice(0, maxVisible);
  const hiddenCount = Math.max(0, imageAttachments.length - visibleAttachments.length);
  const currentIndex = Math.min(selectedIndex, Math.max(0, imageAttachments.length - 1));
  const currentImage = imageAttachments[currentIndex];

  const openAtIndex = (index: number) => {
    setSelectedIndex(index);
    setIsDialogOpen(true);
  };

  const goToPrevious = () => {
    setSelectedIndex(currentIndex > 0 ? currentIndex - 1 : imageAttachments.length - 1);
  };

  const goToNext = () => {
    setSelectedIndex(currentIndex < imageAttachments.length - 1 ? currentIndex + 1 : 0);
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    swipeRef.current = null;
    if (event.touches.length !== 1 || imageAttachments.length < 2 || (window.visualViewport?.scale ?? 1) > 1) return;
    const touch = event.touches[0];
    swipeRef.current = { id: touch.identifier, x: touch.clientX, y: touch.clientY, photoId: currentImage.id };
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    // Once a second finger joins, the whole gesture belongs to native pinch zoom.
    if (event.touches.length !== 1) swipeRef.current = null;
    const start = swipeRef.current;
    if (!start) return;
    const touch = event.touches[0];
    const dx = Math.abs(touch.clientX - start.x);
    const dy = Math.abs(touch.clientY - start.y);
    if (dy >= MIN_SWIPE_DISTANCE && dy > dx) swipeRef.current = null;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start || event.touches.length || start.photoId !== currentImage?.id
        || (window.visualViewport?.scale ?? 1) > 1) return;
    const touch = Array.from(event.changedTouches).find((candidate) => candidate.identifier === start.id);
    if (!touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < MIN_SWIPE_DISTANCE || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx < 0) goToNext();
    else goToPrevious();
  };

  useEffect(() => {
    if (!isDialogOpen || imageAttachments.length <= 1) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDialogOpen, imageAttachments.length, currentIndex]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === viewerRef.current);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    setSelectedIndex((index) => Math.min(index, Math.max(0, imageAttachments.length - 1)));
    if (imageAttachments.length === 0) setIsDialogOpen(false);
  }, [imageAttachments.length]);

  const toggleFullscreen = async () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    if (document.fullscreenElement === viewer) {
      await document.exitFullscreen();
      return;
    }

    await viewer.requestFullscreen();
  };

  const downloadCurrentImage = async () => {
    if (!currentImage || isDownloading) return;

    const fileUrl = attachmentUrl(currentImage);
    const fileName = attachmentName(currentImage);
    setIsDownloading(true);
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to download (${response.status})`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setIsDownloading(false);
    }
  };

  if (imageAttachments.length === 0) return null;

  return (
    <>
      <div className="space-y-2">
        {label && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <ImageIcon className="h-3.5 w-3.5" />
            {label} ({imageAttachments.length})
          </div>
        )}
        <div className={gridClassName}>
          {visibleAttachments.map((attachment, index) => {
            const isMoreTile = hiddenCount > 0 && index === visibleAttachments.length - 1;
            return (
              <div key={attachment.id} className="group relative aspect-square">
                <button
                  type="button"
                  aria-label={`View ${attachmentName(attachment)}`}
                  onClick={() => openAtIndex(index)}
                  className="relative size-full overflow-hidden rounded-md border bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <img
                    src={attachmentUrl(attachment)}
                    alt={attachmentName(attachment)}
                    loading="lazy"
                    onError={handleImageError}
                    className="h-full w-full object-cover transition-transform duration-150 group-hover:scale-[1.02]"
                  />
                  {isMoreTile && (
                    <div className="absolute inset-0 bg-black/55 text-white flex items-center justify-center text-sm font-medium">
                      +{hiddenCount} more
                    </div>
                  )}
                </button>
                {onDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-8 w-8"
                    aria-label={`Remove ${attachmentName(attachment)}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete(attachment.id);
                    }}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={isDialogOpen && imageAttachments.length > 0} onOpenChange={setIsDialogOpen}>
        <DialogContent
          className="flex h-[min(52rem,calc(100dvh-2rem))] max-h-[calc(100dvh-2rem)] min-w-0 max-w-5xl flex-col gap-0 overflow-hidden p-0 transition-none"
          closeButtonClassName="z-20 h-11 w-11 right-2 top-2"
        >
          <DialogTitle className="sr-only">Attachment photo preview</DialogTitle>
          <DialogDescription className="sr-only">
            Preview attached photo in a larger viewer. Swipe left or right to browse photos.
          </DialogDescription>
          {currentImage && (
            <div ref={viewerRef} className="relative flex h-full min-h-0 min-w-0 flex-col bg-black text-white">
              <div className="relative min-h-0 min-w-0 flex-1">
                <div
                  className="absolute inset-0 flex touch-pan-y touch-pinch-zoom items-center justify-center p-2 pt-14 sm:p-6"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={() => { swipeRef.current = null; }}
                >
                  <img
                    src={attachmentUrl(currentImage)}
                    alt={attachmentName(currentImage)}
                    onError={handleImageError}
                    className="h-full w-full min-w-0 object-contain"
                  />
                </div>
                {imageAttachments.length > 1 && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 h-11 w-11 text-white bg-black/35 hover:text-white hover:bg-black/55"
                      aria-label="Previous photo"
                      onClick={goToPrevious}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 h-11 w-11 text-white bg-black/35 hover:text-white hover:bg-black/55"
                      aria-label="Next photo"
                      onClick={goToNext}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </>
                )}
              </div>

              <div className="flex min-w-0 shrink-0 flex-col gap-1 border-t border-white/15 bg-black/70 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-4">
                <p className="min-w-0 truncate text-sm sm:flex-1" title={attachmentName(currentImage)}>
                  {attachmentName(currentImage)}
                </p>
                <div className="flex shrink-0 items-center justify-end gap-2 text-xs">
                  {imageAttachments.length > 1 && (
                    <span className="shrink-0 whitespace-nowrap">
                      {currentIndex + 1} / {imageAttachments.length}
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-11 w-11 shrink-0 p-0 text-white hover:text-white hover:bg-white/15"
                    onClick={toggleFullscreen}
                    title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  >
                    {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-11 w-11 shrink-0 p-0 text-white hover:text-white hover:bg-white/15"
                    title="Download"
                    aria-label="Download"
                    onClick={downloadCurrentImage}
                    disabled={isDownloading}
                  >
                    {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
